import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  useTheme,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuthStore } from "../stores/auth.store";
import { useTranslation } from "react-i18next";
import AuthPageShell from "../components/auth/AuthPageShell";
import AuthSuccessPanel from "../components/auth/AuthSuccessPanel";
import { authFieldSx, authSubmitButtonSx } from "../components/auth/authPage.styles";

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const theme = useTheme();

  const { resetPassword, isLoading, error } = useAuthStore();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    const ok = await resetPassword({
      token,
      new_password: password,
      new_password_confirm: confirm,
    });

    if (ok) {
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2000);
    }
  };

  return (
    <AuthPageShell
      title={t("auth.resetTitle")}
      subtitle={success ? undefined : t("auth.resetSubtitle")}
    >
      {error && (
        <Alert severity="error" sx={{ width: "100%", mb: 3, borderRadius: 1 }}>
          {error}
        </Alert>
      )}

      {success ? (
        <AuthSuccessPanel
          icon={<CheckCircleOutlineIcon sx={{ fontSize: 36 }} />}
          title={t("auth.resetSuccessTitle")}
          message={t("auth.resetSuccess")}
          actionLabel={t("auth.backToSignIn")}
        />
      ) : (
        <>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              required
              type={showPassword ? "text" : "password"}
              label={t("auth.newPassword")}
              margin="normal"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ ...authFieldSx, mt: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      aria-label={
                        showPassword
                          ? t("auth.hidePassword")
                          : t("auth.showPassword")
                      }
                      onClick={() => setShowPassword((value) => !value)}
                    >
                      {showPassword ? (
                        <VisibilityOffOutlinedIcon fontSize="small" />
                      ) : (
                        <VisibilityOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              required
              type={showConfirm ? "text" : "password"}
              label={t("auth.confirmPassword")}
              margin="normal"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              sx={authFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      aria-label={
                        showConfirm
                          ? t("auth.hidePassword")
                          : t("auth.showPassword")
                      }
                      onClick={() => setShowConfirm((value) => !value)}
                    >
                      {showConfirm ? (
                        <VisibilityOffOutlinedIcon fontSize="small" />
                      ) : (
                        <VisibilityOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading || !token}
              sx={authSubmitButtonSx(theme)}
            >
              {isLoading ? t("auth.resetting") : t("auth.resetPassword")}
            </Button>
          </Box>

          <Box sx={{ textAlign: "center", mt: 2.5 }}>
            <Link
              component={RouterLink}
              to="/login"
              underline="hover"
              sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}
            >
              {t("auth.backToSignIn")}
            </Link>
          </Box>
        </>
      )}
    </AuthPageShell>
  );
};

export default ResetPassword;
